import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api';
import './Vendors.css';
import GithubConnectError from '../Vendor/GithubConnectError';

const SI   = 'https://cdn.simpleicons.org';
const WIKI = 'https://upload.wikimedia.org/wikipedia/commons';

const VENDOR_LOGOS = {
  AWS:           'https://aws.amazon.com/favicon.ico',
  GCP:           `${WIKI}/5/51/Google_Cloud_logo.svg`,
  Azure:         `${WIKI}/f/fa/Microsoft_Azure.svg`,
  GitHub:        `${SI}/github/181717`,
  Jira:          `${SI}/jira/0052CC`,
  GitLab:        `${SI}/gitlab/FC6D26`,
  Kubernetes:    `${SI}/kubernetes/326CE5`,
  Datadog:       `${SI}/datadog/632CA6`,
  Okta:          `${SI}/okta/007DC1`,
  Snowflake:     `${SI}/snowflake/29B5E8`,
  Splunk:        `${SI}/splunk/000000`,
  PagerDuty:     `${SI}/pagerduty/06AC38`,
  Bitbucket:     `${SI}/bitbucket/0052CC`,
  DigitalOcean:  `${SI}/digitalocean/0080FF`,
  'New Relic':   `${SI}/newrelic/008C99`,
  'Microsoft 365': `${WIKI}/4/44/Microsoft_logo.svg`,
};

/* Maps the raw vendorName / vendorType strings (any case) to a canonical display name */
const DISPLAY_NAME = {
  aws: 'AWS', gcp: 'GCP', azure: 'Azure', github: 'GitHub', jira: 'Jira',
  gitlab: 'GitLab', kubernetes: 'Kubernetes', datadog: 'Datadog', okta: 'Okta',
  snowflake: 'Snowflake', splunk: 'Splunk', pagerduty: 'PagerDuty',
  bitbucket: 'Bitbucket', digitalocean: 'DigitalOcean', newrelic: 'New Relic',
  crowdstrike: 'CrowdStrike', microsoft: 'Microsoft 365', servicenow: 'ServiceNow',
  slack: 'Slack', 'microsoft 365': 'Microsoft 365', 'new relic': 'New Relic',
};

function VendorInitial({ name }) {
  return (
    <div className="vn-initial" aria-hidden="true">
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

function VendorLogo({ name }) {
  const [broken, setBroken] = useState(false);
  const src = VENDOR_LOGOS[name];
  if (!src || broken) return <VendorInitial name={name} />;
  return (
    <img
      src={src}
      alt={name}
      className="vn-logo"
      onError={() => setBroken(true)}
    />
  );
}

export function toDisplayName(raw) {
  return DISPLAY_NAME[raw?.toLowerCase()] || raw || '—';
}

// Distinct connected-vendor count merged across the old Vendor table and the newer
// VendorConnection table — the single source of truth the Vendors page displays, reused
// by the dashboard KPI so the two screens never disagree.
export function mergedVendorCount(vendorsData, connectionsData) {
  const names = new Set();
  (vendorsData || []).forEach(v => names.add(toDisplayName(v.vendorName).toLowerCase()));
  (connectionsData || []).forEach(c => names.add(toDisplayName(c.vendorType).toLowerCase()));
  return names.size;
}

function Vendors() {
  const { t } = useTranslation();
  const [vendors, setVendors]         = useState([]);
  const [totalCount, setTotalCount]   = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortDir, setSortDir]         = useState('asc');
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const navigate = useNavigate();
  const searchRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [countsRes, vendorsRes, connectionsRes] = await Promise.all([
          axiosInstance.get('/discovered-assets/counts').catch(() => ({ data: {} })),
          axiosInstance.get('/vendors'),
          axiosInstance.get('/vendors/connections').catch(() => ({ data: [] })),
        ]);

        // Per-vendor asset counts from the real discovered-asset inventory (the SAME source
        // the Assets page uses), keyed case-insensitively by cloud provider. This replaces the
        // legacy AWS/GCP/GitHub-only summary, which reported 0 for Azure/M365 and every newer
        // connector — the cause of Vendors showing 0 assets while Assets showed the real count.
        const byProvider = (countsRes.data && countsRes.data.byCloudProvider) || {};
        const assetsByKey = {};
        Object.entries(byProvider).forEach(([name, count]) => {
          assetsByKey[String(name).toLowerCase()] = count;
        });

        const seenNames = new Set();
        const allVendors = [];

        // Old Vendor table (AWS, GCP, Azure, GitHub, Jira)
        (vendorsRes.data || []).forEach(v => {
          const displayName = toDisplayName(v.vendorName);
          const key = displayName.toLowerCase();
          if (!seenNames.has(key)) {
            seenNames.add(key);
            allVendors.push({
              id:     v.id,
              name:   displayName,
              assets: assetsByKey[v.vendorName?.toLowerCase()] || 0,
            });
          }
        });

        // New VendorConnection table (Slack, Datadog, GitLab, …)
        (connectionsRes.data || []).forEach(c => {
          const displayName = toDisplayName(c.vendorType);
          const key = displayName.toLowerCase();
          if (!seenNames.has(key)) {
            seenNames.add(key);
            allVendors.push({
              id:     `conn-${c.id}`,
              name:   displayName,
              assets: assetsByKey[c.vendorType?.toLowerCase()] || 0,
            });
          }
        });

        setVendors(allVendors);
        setTotalCount(allVendors.length);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const exportCSV = () => {
    const rows = [
      ['Vendor', 'Assets'],
      ...displayed.map(v => [v.name, v.assets]),
    ];
    const csv  = rows.map(r => r.join(',')).join('\n');
    const url  = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `vendors_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleSort = () => setSortDir(d => d === 'asc' ? 'desc' : 'asc');

  const displayed = vendors
    .filter(v => !searchQuery || v.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      const cmp = a.name.localeCompare(b.name);
      return sortDir === 'asc' ? cmp : -cmp;
    });

  // The GitHub callback redirects to /dashboard/vendors?githubError=<message> when a connect is
  // refused, and this is the page that receives it. The banner sits above the loading and error
  // branches too: the vendor list is still fetching (or may itself have failed) at exactly the
  // moment the user comes back from GitHub, and the connect error must not be swallowed by either.
  if (loading) {
    return (
      <div className="vn-page">
        <GithubConnectError />
        <div className="vn-loading"><div className="vn-spinner" /></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="vn-page">
        <GithubConnectError />
        <div className="vn-error">{t('Vendors.failedToLoad', { error })}</div>
      </div>
    );
  }

  return (
    <div className="vn-page">
      <GithubConnectError />
      {/* Stat bar */}
      <div className="vn-stat-bar">
        <div className="vn-stat">
          <span className="vn-stat-val">{totalCount}</span>
          <span className="vn-stat-label">{t('Vendors.totalVendors')}</span>
        </div>
        <div className="vn-stat">
          <span className="vn-stat-val">{vendors.reduce((s, v) => s + v.assets, 0)}</span>
          <span className="vn-stat-label">{t('Vendors.totalAssets')}</span>
        </div>
        <div className="vn-stat-spacer" />
        <button className="vn-add-btn" onClick={() => navigate('/dashboard/vendors/cards')}>
          <i className="fas fa-plus" />
          {t('Vendors.addVendor')}
        </button>
      </div>

      {/* Table card */}
      <div className="vn-card">
        {/* Toolbar */}
        <div className="vn-toolbar">
          <div className="vn-search-wrap">
            <i className="fas fa-search vn-search-icon" />
            <input
              ref={searchRef}
              type="text"
              className="vn-search"
              placeholder={t('Vendors.searchPlaceholder')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="vn-search-clear" onClick={() => setSearchQuery('')}>
                <i className="fas fa-times" />
              </button>
            )}
          </div>
          <button className="vn-icon-btn" onClick={exportCSV} title={t('Vendors.exportCsv')}>
            <i className="fas fa-download" />
            <span>{t('Vendors.export')}</span>
          </button>
        </div>

        {/* Table */}
        <div className="vn-table-wrap">
          <table className="vn-table">
            <thead>
              <tr>
                <th className="vn-th-vendor">
                  <button className="vn-sort-btn" onClick={toggleSort}>
                    {t('Vendors.vendorColumn')}
                    <i className={`fas fa-arrow-${sortDir === 'asc' ? 'up' : 'down'} vn-sort-icon`} />
                  </button>
                </th>
                <th>{t('Vendors.assetsColumn')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {displayed.length === 0 ? (
                <tr>
                  <td colSpan={3} className="vn-empty-row">
                    {searchQuery ? t('Vendors.noVendorsMatch') : t('Vendors.noVendorsFound')}
                  </td>
                </tr>
              ) : (
                displayed.map(vendor => (
                  <tr key={vendor.id} className="vn-row">
                    <td>
                      <div className="vn-vendor-cell">
                        <VendorLogo name={vendor.name} />
                        <span className="vn-vendor-name">{vendor.name}</span>
                      </div>
                    </td>
                    <td>
                      <span className="vn-assets">
                        {vendor.assets > 0 ? vendor.assets : <span className="vn-dim">—</span>}
                      </span>
                    </td>
                    <td className="vn-actions-cell">
                      <button
                        className="vn-view-btn"
                        onClick={() => navigate(`/dashboard/vendors/${vendor.name.toLowerCase()}`)}
                      >
                        {t('Vendors.viewDetails')}
                        <i className="fas fa-chevron-right" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="vn-footer">
          <span className="vn-count">
            {t('Vendors.showingCount', { shown: displayed.length, count: vendors.length })}
          </span>
        </div>
      </div>
    </div>
  );
}

export default Vendors;
